/** Briques partagees du portail. */
import { useEffect, useRef, type ReactNode } from "react";

import { ApiError } from "../api";

/** Relit les donnees quand l'utilisateur revient sur l'onglet du portail.
 *
 *  Trouve le 16/09/2026 : une recharge faite dans la console n'apparaissait
 *  pas dans le portail reste ouvert - le solde n'etait lu qu'a l'ouverture
 *  de l'ecran, et l'ecran etait deja ouvert. Un solde perime est trompeur :
 *  il annonce un credit qui n'existe plus, ou en cache un qui existe.
 *
 *  Retour d'onglet ET retour de fenetre : le navigateur emet souvent les
 *  deux evenements pour un seul retour, d'ou l'intervalle minimal. */
export function useRelectureAuRetour(relire: () => void, intervalleMinMs = 1000): void {
  const derniere = useRef(0);
  const relireCourant = useRef(relire);
  relireCourant.current = relire;

  useEffect(() => {
    const auRetour = () => {
      if (document.visibilityState !== "visible") return;
      const maintenant = Date.now();
      if (maintenant - derniere.current < intervalleMinMs) return;
      derniere.current = maintenant;
      relireCourant.current();
    };
    document.addEventListener("visibilitychange", auRetour);
    window.addEventListener("focus", auRetour);
    return () => {
      document.removeEventListener("visibilitychange", auRetour);
      window.removeEventListener("focus", auRetour);
    };
  }, [intervalleMinMs]);
}

/** Centimes -> montant lisible, typographie francaise (espaces
 *  insecables, chiffres alignes en colonne via .num). */
export function montant(minor: number | null | undefined, devise = "EUR"): string {
  if (minor === null || minor === undefined) return "—";
  const signe = minor < 0 ? "-" : "";
  const [entier, cents] = [Math.floor(Math.abs(minor) / 100), Math.abs(minor) % 100];
  const milliers = entier.toLocaleString("fr-FR");
  return `${signe}${milliers},${String(cents).padStart(2, "0")} ${devise}`;
}

export function date(valeur: string | null | undefined, avecHeure = false): string {
  if (!valeur) return "—";
  const d = new Date(valeur);
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    ...(avecHeure ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

/** Message d'erreur ACTIONNABLE : chaque cas appelle une reaction
 *  differente, et "une erreur est survenue" n'en est pas une. */
export function messageErreur(e: unknown): string {
  if (!(e instanceof ApiError)) return "Une erreur inattendue est survenue.";
  switch (e.kind) {
    case "network":
      return "Connexion perdue. Vérifiez votre accès à Internet, puis réessayez.";
    case "unauthorized":
      return "Votre session a expiré. Reconnectez-vous.";
    case "mfa_required":
      return "La double authentification est requise pour votre rôle.";
    case "forbidden":
      return "Votre rôle ne permet pas cette action. Demandez à un administrateur de votre organisation.";
    case "not_found":
      return "Élément introuvable.";
    case "conflict":
      return e.detail ?? "Cette action a déjà été effectuée.";
    case "validation":
      return e.detail ?? "La saisie a été refusée.";
    case "rate_limited":
      return "Trop de requêtes. Patientez quelques instants.";
    default:
      return "Une erreur est survenue de notre côté. Réessayez dans un instant.";
  }
}

export function Erreur({ erreur }: { erreur: unknown }) {
  if (!erreur) return null;
  return (
    <div className="alert alert--error" role="alert">
      {messageErreur(erreur)}
    </div>
  );
}

export function Chargement({ libelle = "Chargement…" }: { libelle?: string }) {
  return (
    <div className="centered" style={{ minHeight: "12rem" }}>
      <div className="spinner" role="status" aria-label={libelle} />
      <p className="muted">{libelle}</p>
    </div>
  );
}

export function Vide({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/** Traduction d'un statut technique en pastille lisible. Les libelles
 *  sont volontairement en francais courant : le client n'a pas a
 *  connaitre notre vocabulaire interne. */
const STATUTS: Record<string, { texte: string; classe: string }> = {
  draft: { texte: "Brouillon", classe: "pill--muted" },
  // Traitement avant signature (12/10/2026) : remplisseurs et valideur.
  prepared: { texte: "Avant signature", classe: "pill--pending" },
  sent: { texte: "Envoyée", classe: "pill--pending" },
  in_progress: { texte: "En cours", classe: "pill--pending" },
  processing: { texte: "Signature en cours", classe: "pill--warn" },
  completed: { texte: "Signée", classe: "pill--ok" },
  cancelled: { texte: "Annulée", classe: "pill--muted" },
  declined: { texte: "Refusée", classe: "pill--err" },
  expired: { texte: "Expirée", classe: "pill--muted" },
  failed: { texte: "Échec", classe: "pill--err" },
  pending: { texte: "En attente", classe: "pill--muted" },
  notified: { texte: "Invité", classe: "pill--pending" },
  viewed: { texte: "Document consulté", classe: "pill--pending" },
  authenticated: { texte: "Identité vérifiée", classe: "pill--pending" },
  signed: { texte: "Signé", classe: "pill--ok" },
};

/** Resultat d'une enveloppe (decision du 17/09/2026) : succes ou echec, et
 *  son motif. Rien tant que l'enveloppe est en cours. */
export function Resultat({ outcome, motif }: { outcome?: string | null; motif?: string | null }) {
  if (outcome !== "success" && outcome !== "failure") return null;
  return (
    <span className={`pill ${outcome === "success" ? "pill--ok" : "pill--err"}`} title={motif ?? undefined}>
      {outcome === "success" ? "Succès" : "Échec"}
    </span>
  );
}

export function Statut({ valeur }: { valeur: string | null | undefined }) {
  const s = STATUTS[valeur ?? ""] ?? { texte: valeur ?? "—", classe: "pill--muted" };
  return <span className={`pill ${s.classe}`}>{s.texte}</span>;
}

/** Telechargement d'un blob deja recupere. Passe par une URL objet
 *  locale : le fichier ne transite par aucun service tiers. */
export function telecharger(blob: Blob, nom: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nom;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Icones traits du portail (une seule famille : 16 px, trait 1,4). Critique
 *  impeccable du 13/10/2026 : plus d'emoji ni de glyphe unicode en icone. */
export const TRACES = {
  haut: "M8 13V3m0 0L4 7m4-4 4 4",
  bas: "M8 3v10m0 0-4-4m4 4 4-4",
  croix: "M4 4l8 8M12 4l-8 8",
  coche: "M3.5 8.5l3 3 6-7",
  enveloppe: "M2 4h12v8H2zM2 4l6 5 6-5",
  gauche: "M13 8H3m0 0 4-4M3 8l4 4",
  // Replier / deplier un bloc (14/10/2026) : chevrons, distincts des fleches
  // « monter / descendre ».
  chevronHaut: "M4 10l4-4 4 4",
  chevronBas: "M4 6l4 4 4-4",
} as const;

export function Icone({ d, taille = 18 }: { d: string; taille?: number }) {
  return (
    <svg viewBox="0 0 16 16" width={taille} height={taille} aria-hidden="true" focusable="false">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
